import { useState } from "react";
import type { ContextRuleView } from "@bitacorapro/api-types";
import { useResolveContextConflict } from "../lib/api/queries";
import { Drawer } from "./Drawer";

// Resolve a slot conflict (ADR-0173 fase 2, PR-7): two or more rules of
// the brand set the same (item, parameter, scope) to different values. The
// reviewer keeps one; the others are discarded atomically on the server
// (POST /context-rules/:id/resolve-conflict). Precedence between document
// kinds is only a proposal here — the person decides.

type Props = {
  group: ContextRuleView[];
  onClose: () => void;
  onResolved: (kept: ContextRuleView) => void;
};

/** Rules in conflict share a slot: same item code + same param name/scope.
 *  The server flags members individually; this regroups them for the UI. */
export function groupConflicts(rules: ContextRuleView[]): ContextRuleView[][] {
  const bySlot = new Map<string, ContextRuleView[]>();
  for (const r of rules) {
    if (r.status !== "in_conflict") continue;
    for (const code of r.itemCodes) {
      for (const p of r.version.params) {
        const key = `${code}\u0000${p.name}\u0000${p.scope ?? ""}`;
        const list = bySlot.get(key) ?? [];
        if (!list.includes(r)) list.push(r);
        bySlot.set(key, list);
      }
    }
  }
  const seen = new Set<string>();
  const groups: ContextRuleView[][] = [];
  for (const members of bySlot.values()) {
    const sig = members
      .map((m) => m.id)
      .sort()
      .join(",");
    if (seen.has(sig)) continue;
    seen.add(sig);
    groups.push(members);
  }
  // A rule flagged in_conflict whose partner was already discarded/edited
  // forms a group of one: resolving it just accepts it.
  for (const r of rules) {
    if (r.status === "in_conflict" && !groups.some((g) => g.includes(r))) groups.push([r]);
  }
  return groups;
}

export function ContextConflictDrawer({ group, onClose, onResolved }: Props) {
  const [keepId, setKeepId] = useState<string>(group[0]?.id ?? "");
  const [error, setError] = useState<string | null>(null);
  const resolve = useResolveContextConflict();
  const kept = group.find((r) => r.id === keepId);

  async function submit() {
    if (!kept) return;
    setError(null);
    try {
      const out = await resolve.mutateAsync({
        id: kept.id,
        discardRuleIds: group.filter((r) => r.id !== kept.id).map((r) => r.id),
      });
      onResolved(out);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo resolver.");
    }
  }

  return (
    <Drawer
      title="Resolver conflicto"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!kept || resolve.isPending}
            onClick={() => void submit()}
          >
            {resolve.isPending ? "Resolviendo…" : "Conservar esta regla"}
          </button>
        </>
      }
    >
      <p className="cell-meta">
        Estas reglas fijan valores distintos para el mismo ítem y parámetro. La que conserves queda
        aceptada; las demás se descartan.
      </p>
      <div className="stack-gap" role="radiogroup" aria-label="Regla a conservar">
        {group.map((r) => (
          <label key={r.id} className="field" style={{ flexDirection: "row", gap: "var(--s-3)" }}>
            <input
              type="radio"
              name="keep"
              checked={keepId === r.id}
              onChange={() => setKeepId(r.id)}
            />
            <span>
              <span className="cell-strong">{r.version.text}</span>
              <span className="cell-meta">
                {" "}
                · ítems {r.itemCodes.join(", ") || "—"}
                {r.version.params.length > 0 &&
                  ` · ${r.version.params.map((p) => `${p.name} ${p.op} ${String(p.value)}`).join(", ")}`}
              </span>
            </span>
          </label>
        ))}
      </div>
      {error && (
        <div className="empty" role="alert">
          <div className="empty__title">No se pudo resolver</div>
          <div className="empty__sub">{error}</div>
        </div>
      )}
    </Drawer>
  );
}
